/**
 * The grantor's half of an errand (VS3): how much text fits, how long it may
 * last, and the signature.
 *
 * The verifier's half is `verifyErrand` in `verify.ts`. Nothing in this file
 * is enforced anywhere else: the issuer never sees an errand, and a verifier
 * only enforces the fixed 140-byte cap. Keeping the bearer's presentation at
 * QR version 26 is this wallet's job, the way keeping the identity at version
 * 20 is the issuer's.
 */
import { ERRAND_TEXT_MAX, MODE_FAST, NONCE_LEN, SIGNATURE_LEN } from "./constants";
import { signWithDevice } from "./crypto";
import { encodeErrand, encodePresentation, errandSigningInput } from "./format";

/**
 * Bytes a version-26 QR holds at level L with the wallet's writer, measured
 * like version 20 (spec, «El presupuesto») and pinned in `tests/errand.test.ts`.
 * 121 modules, 3.1 pixels per module on 400 screen pixels.
 */
export const QR_V26_CAPACITY = 1365;
/** Below this many bytes of text the wallet does not offer the errand at all:
 *  nothing useful can be asked in less. */
export const ERRAND_TEXT_FLOOR = 20;
export const ERRAND_DEFAULT_TTL_S = 86_400;
export const ERRAND_MAX_TTL_S = 7 * 86_400;
/** The lifetimes the form offers, 24 hours first because it is the default. */
export const ERRAND_TTL_CHOICES: { seconds: number; label: string }[] = [
  { seconds: ERRAND_DEFAULT_TTL_S, label: "24 horas" },
  { seconds: 3600, label: "1 hora" },
  { seconds: 3 * 86_400, label: "3 días" },
  { seconds: ERRAND_MAX_TTL_S, label: "7 días" },
];

/** The widest value a timestamp or an expiry takes on this wire before 2106:
 *  a four-byte CBOR unsigned integer, the width every real one has. */
const WIDEST_UINT = 0xffff_ffff;

export function textBytes(text: string): number {
  return new TextEncoder().encode(text).length;
}

/**
 * How many bytes of text this errand can carry and still leave the bearer's
 * presentation inside version 26: `min(140, bytes left before 1365)`, never
 * below zero.
 *
 * The probe is the presentation the bearer will build, with every field that
 * is not known yet at its widest: an empty text, zero nonce and signatures,
 * and four-byte time fields. Subtracting its length from the capacity is
 * almost the answer, but not quite: the text's CBOR head is one byte up to 23
 * bytes of text and two from 24, so the first guess is checked by encoding it
 * and stepped down until it fits. That loop runs at most twice.
 */
export function errandTextBudget(args: {
  bearerCredentialBytes: Uint8Array;
  liteCredentialBytes: Uint8Array;
  bearer: string;
}): number {
  const length = (bytes: number) => encodePresentation({
    credentialBytes: args.bearerCredentialBytes,
    timestamp: WIDEST_UINT,
    nonce: new Uint8Array(NONCE_LEN),
    mode: MODE_FAST,
    signature: new Uint8Array(SIGNATURE_LEN),
    errandBytes: encodeErrand({
      credentialBytes: args.liteCredentialBytes,
      text: "a".repeat(bytes),
      bearer: args.bearer,
      expiresAt: WIDEST_UINT,
      signature: new Uint8Array(SIGNATURE_LEN),
    }),
  }).length;
  let budget = Math.min(ERRAND_TEXT_MAX, QR_V26_CAPACITY - length(0));
  while (budget > 0 && length(budget) > QR_V26_CAPACITY) budget -= 1;
  return Math.max(0, budget);
}

/** The errand's expiry: the chosen lifetime, never more than seven days and
 *  never past the grantor's own credential, which a verifier would refuse
 *  from that second on anyway. */
export function errandExpiry(now: number, ttlSeconds: number, grantorExpiresAt: number): number {
  return Math.min(now + Math.min(ttlSeconds, ERRAND_MAX_TTL_S), grantorExpiresAt);
}

/**
 * Sign and encode. The cap is checked here because `encodeErrand` does not
 * check it, and an errand over it is one every verifier refuses as malformed.
 */
export async function createErrand(args: {
  liteCredentialBytes: Uint8Array;
  text: string;
  bearer: string;
  expiresAt: number;
  deviceKey: CryptoKey;
}): Promise<Uint8Array> {
  const size = textBytes(args.text);
  if (size > ERRAND_TEXT_MAX) {
    throw new Error(`errand text is ${size} bytes of UTF-8, the cap is ${ERRAND_TEXT_MAX}`);
  }
  const fields = {
    credentialBytes: args.liteCredentialBytes,
    text: args.text,
    bearer: args.bearer,
    expiresAt: args.expiresAt,
  };
  const signature = await signWithDevice(args.deviceKey, errandSigningInput(fields));
  return encodeErrand({ ...fields, signature });
}
